import { useForm } from "@tellescope/forms";
import { useCachedTellescopeQueries, useVitalsQueryWithFilters } from "@tellescope/tanstack-query";
import {
  DateRangeField,
  Label,
  MultiSelect,
  MultiSelectOption,
  PaginatedList,
  RadioGroupButtonItem,
  RadioGroupButtons,
} from "@tellescope/ui";
import { type ObservationType } from "@tellescope/utils";
import { useMemo, type Dispatch, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { useVitalProgramsAndThresholds } from "../../../../hooks";
import { VitalsChart } from "./VitalsChart/VitalsChart";
import { VitalsSectionItem } from "./VitalsSectionItem";
import { useVitalsChart } from "./VitalsChart";

const MS_IN_DAY = 1000 * 60 * 60 * 24;

const OBSERVATION_TYPE_UNITS: Record<ObservationType, string[] | undefined> = {
  "blood-pressure": ["SBP", "DBP", "BPM"],
  weight: ["LB"],
  height: ["Height"],
  "heart-rate": ["BPM"],
  glucose: ["mg/dL"],
  unknown: undefined,
};

const MED_STATUS_VALUES: Record<string, string | undefined> = {
  all: undefined,
  before: "Medication: Before Meds",
  after: "Medication: After Meds",
};

const MEAL_STATUS_VALUES: Record<string, boolean | undefined> = {
  all: undefined,
  before: true,
  after: false,
};

export interface VitalsProgramSubsectionProps {
  enduserId: string;
  observationType: ObservationType;
  unit?: string;
  observationId: string | undefined;
  setObservationId: Dispatch<SetStateAction<string | undefined>>;
  chartHeight?: string;
}

export const VitalsProgramSubsection = ({
  enduserId,
  observationType,
  unit,
  observationId,
  setObservationId,
  chartHeight,
}: VitalsProgramSubsectionProps) => {
  const { t } = useTranslation("vitals");
  const { thresholdsFromPrograms } = useVitalProgramsAndThresholds();

  const units = OBSERVATION_TYPE_UNITS[observationType];

  const selectedThresholdIds = useMemo(() => {
    if (!units) return undefined;
    const unitSet = new Set(units);
    return thresholdsFromPrograms.filter((threshold) => unitSet.has(threshold.unit)).map((threshold) => threshold.id);
  }, [thresholdsFromPrograms, units]);

  const devices = useCachedTellescopeQueries({
    model: "enduser_observations",
    method: "getSome",
    select: (queries) =>
      queries.reduce<Set<string>>((acc, curr) => {
        curr.state.data?.pages.forEach((page) =>
          page.forEach((observation) => {
            if (observation.source) {
              acc.add(observation.source);
            }
          })
        );
        return acc;
      }, new Set<string>()),
  });

  const filtersForm = useForm({
    fields: {
      dateRange: {
        type: "dateRange",
        initialValue: {
          from: undefined,
          to: undefined,
        },
      },
      meds: {
        type: "radio",
        initialValue: "all",
      },
      meal: {
        type: "radio",
        initialValue: "all",
      },
      devices: {
        type: "multiselect",
        initialValue: [],
      },
    },
  });

  const medStatus = MED_STATUS_VALUES[filtersForm.values.meds];
  const beforeMeal = MEAL_STATUS_VALUES[filtersForm.values.meal];

  const observationsQuery = useVitalsQueryWithFilters({
    limit: 50,
    filters: {
      enduserIds: [enduserId],
      units: observationType === "unknown" && unit ? [unit] : units,
      dateRange: {
        from: filtersForm.values.dateRange.from ? new Date(filtersForm.values.dateRange.from) : undefined,
        to: filtersForm.values.dateRange.to ? new Date(filtersForm.values.dateRange.to + MS_IN_DAY) : undefined,
      },
      sources: filtersForm.values.devices.length > 0 ? filtersForm.values.devices : undefined,
      ...(medStatus !== undefined && { medStatus }),
      ...(beforeMeal !== undefined && { beforeMeal }),
      validStatus: "valid",
    },
    combineRelatedObservations: true,
  });

  const deviceOptions = useMemo<MultiSelectOption<string>[]>(() => {
    return Array.from(devices.data).map((name) => ({ label: name, value: name }));
  }, [devices.data]);

  const filteredObservations = useMemo(() => {
    if (!observationsQuery.data) return observationsQuery.data;

    switch (observationType) {
      case "blood-pressure": {
        const allowedUnits = new Set(["SBP", "DBP", "BPM"]);
        return observationsQuery.data.filter((obs) => {
          const obsUnits = new Set(obs.measurements.map((m) => m.unit));
          return obsUnits.has("SBP") && obsUnits.has("DBP") && [...obsUnits].every((u) => allowedUnits.has(u));
        });
      }
      case "heart-rate":
        return observationsQuery.data.filter((obs) => obs.measurements.every((m) => m.unit === "BPM"));
      case "weight":
      case "height":
      case "glucose":
      case "unknown":
        return observationsQuery.data;
    }
  }, [observationsQuery.data, observationType]);

  const vitalsChartProps = useVitalsChart({
    observations: filteredObservations,
    thresholds: thresholdsFromPrograms,
    selectedThresholdIds: selectedThresholdIds,
    observationType: observationType,
    observationId: observationId,
    setObservationId: setObservationId,
  });

  return (
    <div className="overflow-hidden flex flex-col">
      <div className="px-4">
        <div className="border border-gray-200 rounded-lg bg-gray-50/50 p-3 mb-3 flex items-center gap-4">
          <span className="flex items-center gap-2">
            <Label className="text-muted-foreground font-normal text-xs">{t("Date Range:")}</Label>
            <DateRangeField {...filtersForm.getCustomFieldProps("dateRange")} placeholder={t("Pick dates")} size="xs" />
          </span>
          {observationType === "blood-pressure" && (
            <span className="flex items-center gap-2">
              <Label className="text-muted-foreground font-normal text-xs">{t("Meds:")}</Label>
              <RadioGroupButtons {...filtersForm.getCustomFieldProps("meds")} className="flex items-center gap-0">
                <RadioGroupButtonItem value="all" className="px-2 text-xs h-6 font-normal">
                  {t("All")}
                </RadioGroupButtonItem>
                <RadioGroupButtonItem value="before" className="px-2 text-xs h-6 font-normal">
                  {t("Before")}
                </RadioGroupButtonItem>
                <RadioGroupButtonItem value="after" className="px-2 text-xs h-6 font-normal">
                  {t("After")}
                </RadioGroupButtonItem>
              </RadioGroupButtons>
            </span>
          )}
          {observationType === "glucose" && (
            <span className="flex items-center gap-2">
              <Label className="text-muted-foreground font-normal text-xs">{t("Meal:")}</Label>
              <RadioGroupButtons {...filtersForm.getCustomFieldProps("meal")} className="flex items-center gap-1">
                <RadioGroupButtonItem value="all" className="px-2 text-xs h-6">
                  {t("All")}
                </RadioGroupButtonItem>
                <RadioGroupButtonItem value="before" className="px-2 text-xs h-6">
                  {t("Before")}
                </RadioGroupButtonItem>
                <RadioGroupButtonItem value="after" className="px-2 text-xs h-6">
                  {t("After")}
                </RadioGroupButtonItem>
              </RadioGroupButtons>
            </span>
          )}
          <span className="flex items-center gap-2">
            <Label className="text-muted-foreground font-normal text-xs">{t("Device:")}</Label>
            <MultiSelect
              {...filtersForm.getCustomFieldProps("devices")}
              options={deviceOptions}
              className="h-6 min-h-0 text-xs py-0 px-2"
              placeholder={t("All devices")}
              minPopoverWidth={200}
              showAsBadges={false}
            />
          </span>
        </div>
      </div>
      <VitalsChart {...vitalsChartProps} className="border-b-gray-200 border-b pb-2 px-4" height={chartHeight} />
      <PaginatedList className="w-full p-4" onBottomReached={() => observationsQuery.fetchNextPage()}>
        {filteredObservations?.map((observation) => (
          <VitalsSectionItem
            key={observation.id}
            observation={observation}
            className="w-full mb-2"
            onClick={() => setObservationId(observation.id === observationId ? undefined : observation.id)}
            selectedThresholdIds={selectedThresholdIds}
            expanded={observation.id === observationId}
          />
        ))}
      </PaginatedList>
    </div>
  );
};
